/* AK VAT toggle — Phase 1 (state plumbing only).
 *
 * - Reads/writes the ak_vat_mode cookie (values: 'ex' | 'inc', max-age 1 year, path /).
 * - Swaps the html.vat-mode-ex|inc class on click.
 * - Dispatches `ak:vat-mode-changed` so any dynamic widget (e.g. the Mode A
 *   calculator in Phase 3) can re-emit its output from a cached ex-VAT base.
 *
 * The inline head script in layout/theme.liquid sets the initial html class
 * before any paint, so this file's only paint-time responsibility is to
 * re-sync the toggle button [aria-pressed] state against the live cookie
 * (in case the cookie changed in another tab between render and load).
 */
(function () {
  'use strict';

  var COOKIE_NAME = 'ak_vat_mode';
  var COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year
  var EVENT_NAME = 'ak:vat-mode-changed';
  var VALID_MODES = ['ex', 'inc'];

  // Single client-side source of truth for the UK standard VAT rate.
  // Companion Liquid constant is at the top of snippets/ak-vat-price.liquid.
  // To change the rate (e.g. HMRC change), update BOTH this and the snippet.
  window.AK_VAT = {
    multiplier: 1.20,
    rate: 0.20,
    eventName: EVENT_NAME,
    getMode: function () {
      var m = document.cookie.match(/(?:^|;\s*)ak_vat_mode=(ex|inc)/);
      return m ? m[1] : 'inc'; // default inc VAT (2026-10-16)
    },
    /**
     * Returns the AK dual-span price markup for a given ex-VAT pennies
     * integer. Matches the shape emitted by snippets/ak-vat-price.liquid
     * (.ak-price wrapper > .ak-price__ex / .ak-price__inc / .ak-price__label).
     * CSS in assets/ak-vat.css hides one figure based on html.vat-mode-*.
     *
     * Use this from any client-rendered surface that needs to show a
     * VAT-toggleable price — currently the Mode A calculator, in future
     * any dynamic cart preview / quick-add modal.
     *
     * @param {number} penniesEx — Ex-VAT price in pennies. Integer.
     * @returns {string} HTML string ready for innerHTML insertion.
     */
    dualSpan: function (penniesEx) {
      var ex = penniesEx | 0;
      var inc = Math.round(ex * this.multiplier);
      function gbp(c) {
        return '£' + (c / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
      return (
        '<span class="ak-price" data-ak-price data-ex-pennies="' + ex + '">' +
          '<span class="ak-price__ex">' + gbp(ex) + '</span>' +
          '<span class="ak-price__inc">' + gbp(inc) + '</span>' +
          '<span class="ak-price__label" aria-hidden="true"></span>' +
        '</span>'
      );
    }
  };

  function readCookie() {
    var m = document.cookie.match(/(?:^|;\s*)ak_vat_mode=(ex|inc)/);
    return m ? m[1] : 'inc'; // default inc VAT (2026-10-16)
  }

  function writeCookie(mode) {
    document.cookie =
      COOKIE_NAME + '=' + mode +
      '; max-age=' + COOKIE_MAX_AGE +
      '; path=/; SameSite=Lax';
  }

  function applyMode(mode) {
    var d = document.documentElement;
    d.classList.remove('vat-mode-ex', 'vat-mode-inc');
    d.classList.add('vat-mode-' + mode);

    var toggles = document.querySelectorAll('[data-ak-vat-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      var btns = toggles[i].querySelectorAll('[data-ak-vat-mode]');
      for (var j = 0; j < btns.length; j++) {
        var b = btns[j];
        b.setAttribute('aria-pressed', b.getAttribute('data-ak-vat-mode') === mode ? 'true' : 'false');
      }
    }
  }

  function setMode(mode) {
    if (VALID_MODES.indexOf(mode) === -1) return;
    if (mode === readCookie()) return;
    writeCookie(mode);
    applyMode(mode);
    document.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { mode: mode } }));
  }

  function init() {
    // Re-sync DOM with the live cookie (cookie may have changed in another tab).
    applyMode(readCookie());

    var toggles = document.querySelectorAll('[data-ak-vat-toggle]');
    for (var i = 0; i < toggles.length; i++) {
      toggles[i].addEventListener('click', function (e) {
        var btn = e.target.closest('[data-ak-vat-mode]');
        if (!btn || !this.contains(btn)) return;
        setMode(btn.getAttribute('data-ak-vat-mode'));
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
